import { moment, mui } from '../../libs';

export const valueToDates = (value: [string, string]) => {
  const lval = [null, null] as mui.date.DateRange<Date>;
  if (value?.[0]) lval[0] = moment(value[0]).toDate();
  if (value?.[1]) lval[1] = moment(value[1]).toDate();
  return lval;
};

export const datesToValue = (d: mui.date.DateRange<Date>) => {
  const lval = [null, null] as string[];
  const [startRaw, endRaw] = d;
  const start = moment(startRaw);
  const end = moment(endRaw);
  if (start.isValid()) lval[0] = start.format('YYYY-MM-DD');
  if (end.isValid()) lval[1] = end.format('YYYY-MM-DD');
  return lval;
};

export const getNearest = (d: string, validDates: string[], startIdx: number, endIdx: number): Date => {
  if (startIdx > endIdx) return moment(validDates[Math.max(0, endIdx)]).toDate();
  const mid = Math.floor((startIdx + endIdx) / 2);

  if (d == validDates[mid]) return moment(validDates[mid]).toDate();
  if (moment(d).isAfter(moment(validDates[mid]))) return getNearest(d, validDates, mid + 1, endIdx);
  if (moment(d).isBefore(moment(validDates[mid]))) return getNearest(d, validDates, startIdx, mid - 1);
};

export const clampRangeIfNeeded = (
  v: mui.date.DateRange<Date>,
  lastEdited: 'start' | 'end' | null
): mui.date.DateRange<Date> => {
  const [startRaw, endRaw] = v;
  if (!startRaw || !endRaw) return v;

  const start = moment(startRaw);
  const end = moment(endRaw);
  if (!start.isValid() || !end.isValid()) return v;
  if (!start.isAfter(end)) return v;

  if (lastEdited === 'end') return [end.toDate(), end.toDate()];
  return [start.toDate(), start.toDate()];
};

export const getValidValue = (
  value: mui.date.DateRange<Date>,
  validDates: string[],
  allValidDates: string[]
): mui.date.DateRange<Date> => {
  if (!validDates || !allValidDates) return value;

  const startVal = value[0]
    ? getNearest(moment(value[0]).format('YYYY-MM-DD'), validDates, 0, validDates.length - 1)
    : null;
  const endVal = value[1]
    ? getNearest(moment(value[1]).format('YYYY-MM-DD'), allValidDates, 0, allValidDates.length - 1)
    : null;
  return [startVal, endVal];
};

export type TransformRangeInput = {
  range: mui.date.DateRange<Date>;
  validDates?: string[];
  allValidDates?: string[];
  minDate?: string;
  maxDate?: string;
  onlyStartDate?: boolean;
  onlyEndDate?: boolean;
  lastEdited?: 'start' | 'end' | null;
};

export type TransformRangeResult = {
  range: mui.date.DateRange<Date>;
  errors: [boolean, boolean];
  changed: boolean;
};

const dateEq = (a: Date, b: Date) => (a === b ? true : !!a && !!b && a.getTime() === b.getTime());
const rangeEq = (a: mui.date.DateRange<Date>, b: mui.date.DateRange<Date>) => dateEq(a[0], b[0]) && dateEq(a[1], b[1]);

export const transformRangeBeforeCommit = ({
  range,
  validDates,
  allValidDates,
  minDate,
  maxDate,
  onlyStartDate = false,
  onlyEndDate = false,
  lastEdited = null,
}: TransformRangeInput): TransformRangeResult => {
  const startInvalid = range[0]?.toString() === 'Invalid Date';
  const endInvalid = range[1]?.toString() === 'Invalid Date';
  if (startInvalid || endInvalid) {
    return {
      range,
      errors: [startInvalid, endInvalid],
      changed: false,
    };
  }

  const requireStart = !onlyEndDate;
  const requireEnd = !onlyStartDate;

  const startEmpty = !range[0];
  const endEmpty = !range[1];

  let next: mui.date.DateRange<Date> = range;
  let didHydrate = false;

  if (requireStart && startEmpty) {
    const startFallback = validDates?.[0] ?? minDate;
    if (startFallback) {
      next = [moment(startFallback).toDate(), next[1]];
      didHydrate = true;
    }
  }

  if (requireEnd && endEmpty) {
    const endFallback = allValidDates?.[allValidDates.length - 1] ?? maxDate;
    if (endFallback) {
      next = [next[0], moment(endFallback).toDate()];
      didHydrate = true;
    }
  }

  const snapped = getValidValue(next, validDates, allValidDates);
  const clamped = clampRangeIfNeeded(snapped, lastEdited);

  return {
    range: clamped,
    errors: [false, false],
    changed: didHydrate || !rangeEq(range, clamped),
  };
};
